import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { createRoot } from "react-dom/client";

export type NotificationType = "success" | "error" | "info" | "warning";

interface NotificationProps {
  message: string;
  type?: NotificationType;
  duration?: number;
  onClose: () => void;
  placement?:
    | "top"
    | "bottom"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight";
}

const getIcon = (type: NotificationType) => {
  switch (type) {
    case "success":
      return "✓";
    case "error":
      return "✕";
    case "warning":
      return "⚠";
    case "info":
      return "ℹ";
    default:
      return "ℹ";
  }
};

const getIconColor = (type: NotificationType) => {
  switch (type) {
    case "success":
      return "#52c41a";
    case "error":
      return "#ff4d4f";
    case "warning":
      return "#faad14";
    case "info":
      return "#1890ff";
    default:
      return "#1890ff";
  }
};

export const Notification: React.FC<NotificationProps> = ({
  message,
  type = "info",
  duration = 4500,
  onClose,
  placement = "top",
}) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(progressInterval);
          return 0;
        }
        return prev - 100 / (duration / 100);
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [duration, onClose]);

  return createPortal(
    <div className={classNames(styles.customSnackbar, styles[placement])}>
      <div
        className={styles.iconCircle}
        style={{
          background: `linear-gradient(135deg, ${getIconColor(type)} 0%, ${getIconColor(type)}80 100%)`,
        }}
      >
        <span style={{ color: "white", fontSize: "16px", fontWeight: "bold" }}>
          {getIcon(type)}
        </span>
      </div>

      <span className={styles.snackbarText}>{message}</span>

      <button className={styles.closeBtn} onClick={onClose}>
        ×
      </button>

      <div className={styles.progressBar}>
        <div
          className={styles.progress}
          style={{
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${getIconColor(type)} 0%, ${getIconColor(type)}80 100%)`,
          }}
        />
      </div>
    </div>,
    document.body,
  );
};

const notificationContainer = document.createElement("div");
notificationContainer.id = "notification-container";
document.body.appendChild(notificationContainer);

let notifications: { id: number; element: React.ReactNode }[] = [];
let nextId = 0;

export const showNotification = (
  message: string,
  type: NotificationType = "info",
  duration: number = 4500,
  placement: NotificationProps["placement"] = "topRight",
) => {
  const id = nextId++;
  const removeNotification = () => {
    notifications = notifications.filter((n) => n.id !== id);
    render();
  };

  const notification = (
    <Notification
      key={id}
      message={message}
      type={type}
      duration={duration}
      onClose={removeNotification}
      placement={placement}
    />
  );

  notifications.push({ id, element: notification });
  render();
};

let root: ReturnType<typeof createRoot> | null = null;
function render() {
  const container = document.getElementById("notification-container");
  if (container) {
    if (!root) {
      root = createRoot(container);
    }
    root.render(<>{notifications.map(({ element }) => element)}</>);
  }
}
